import { useBrowserOnline, useConnectionStore } from "@api/connectionStore";
import { useReachabilityProbe } from "@api/reachabilityMiddleware";
import { Band, TriangleExclamationIcon } from "@scribenote/brian-design-system";

const COPY = {
  device: {
    heading: "No connection",
    detail:
      "This computer is off the network. Brian picks up where it left off once it’s back.",
  },
  server: {
    heading: "Can’t reach Brian",
    detail: "The network is up but Brian isn’t answering. Still trying.",
  },
} as const;

/**
 * react-query pauses queries while offline, so `useSuspenseQuery` suspends with no error for a boundary to show.
 * Not dismissable, and mounted outside `AppShell` (login too) since it also drives the recovery probe.
 */
export const OfflineNotice = () => {
  useReachabilityProbe();
  const browserOnline = useBrowserOnline();
  const serverUnreachable = useConnectionStore((s) => s.serverUnreachable);

  if (browserOnline && !serverUnreachable) return null;

  const copy = browserOnline ? COPY.server : COPY.device;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-notice"
      className="z-toast fixed bottom-4 left-4 w-72 overflow-hidden rounded-paper border border-rule bg-card shadow-lift sm:bottom-6 sm:left-12"
    >
      <Band
        tone="stopped"
        icon={<TriangleExclamationIcon />}
        className="text-annotation-caps"
      >
        {copy.heading}
      </Band>
      <p className="px-3 py-2 text-sm text-ink-body">{copy.detail}</p>
    </div>
  );
};
